import React, { useEffect, useState, useCallback } from 'react';
import type { ImpersonationMode } from '@court-sync/shared';
import {
    Card,
    CardHeader,
    CardContent,
    PaginatedTable,
    Button,
    Input,
    Modal,
    Field,
    FieldLabel,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../../components/ui';
import type { PaginationState } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Search, Trash2, AlertTriangle, Loader2, Link2, Eye, Pencil } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { enterImpersonation } from '../../services/impersonation';

interface UserRoleWithAcademy {
    role: string;
    academyId?: string | null;
}

interface UserItem {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName: string;
    roles?: string[];
    rolesWithAcademy?: UserRoleWithAcademy[];
    primaryRole?: string;
    academyId?: string;
    createdAt: string | Date;
}

const LINK_ROLE_OPTIONS: { value: 'academy_owner' | 'coach' | 'guardian' | 'student'; label: string }[] = [
  { value: 'academy_owner', label: 'Academy owner' },
  { value: 'coach', label: 'Coach' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'student', label: 'Student' },
];

interface AcademySearchHit {
  id: string;
  name: string;
  slug: string;
}

const ROLE_BADGE_CLASS: Record<string, string> = {
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    academy_owner: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    coach: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    guardian: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    student: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

function formatRole(role: string): string {
    return (role ?? '').replace(/_/g, ' ').toUpperCase() || '–';
}

export default function UsersPage() {
    const navigate = useNavigate();
    const [users, setUsers] = useState<UserItem[]>([]);
    const [pagination, setPagination] = useState<PaginationState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Delete confirmation state
    const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState('');

    const [linkUserId, setLinkUserId] = useState<string | null>(null);
    const [linkRole, setLinkRole] = useState<'academy_owner' | 'coach' | 'guardian' | 'student'>('guardian');
    const [academyQuery, setAcademyQuery] = useState('');
    const [debouncedAcademyQuery, setDebouncedAcademyQuery] = useState('');
    const [academyHits, setAcademyHits] = useState<AcademySearchHit[]>([]);
    const [selectedAcademy, setSelectedAcademy] = useState<AcademySearchHit | null>(null);
    const [academySearchLoading, setAcademySearchLoading] = useState(false);
    const [linkSubmitting, setLinkSubmitting] = useState(false);
    const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(null);
    const [readWriteWarningUserId, setReadWriteWarningUserId] = useState<string | null>(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Reset to first page on search
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedAcademyQuery(academyQuery), 350);
        return () => clearTimeout(timer);
    }, [academyQuery]);

    useEffect(() => {
        if (!linkUserId) {
            return undefined;
        }
        const q = debouncedAcademyQuery.trim();
        if (q.length < 2) {
            setAcademyHits([]);
            return undefined;
        }
        let cancelled = false;
        setAcademySearchLoading(true);
        adminBackofficeApi
            .searchAcademies({ q, limit: 20 })
            .then((json: { data?: { academies?: AcademySearchHit[] } }) => {
                if (cancelled) return;
                const list = json.data?.academies ?? [];
                setAcademyHits(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (!cancelled) setAcademyHits([]);
            })
            .finally(() => {
                if (!cancelled) setAcademySearchLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [debouncedAcademyQuery, linkUserId]);

    const loadUsers = useCallback(() => {
        setLoading(true);
        adminBackofficeApi
            .getUsers({ page, limit: 20, search: debouncedSearch })
            .then((data) => {
                if (data.success && data.data) {
                    setUsers(data.data.users || []);
                    setPagination(data.data.pagination || null);
                } else {
                    setError(data.error || 'Failed to load users');
                }
            })
            .catch((err) => {
                setError(err?.response?.data?.error || err?.message || 'Failed to load users');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [page, debouncedSearch]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const handleDeleteUser = async () => {
        if (!deleteUserId) return;

        const user = users.find(u => u.id === deleteUserId);
        if (confirmEmail !== user?.email) {
            toast.error('Email confirmation does not match');
            return;
        }

        setDeleting(true);
        try {
            const res = await adminBackofficeApi.deleteUser(deleteUserId);
            if (res.success) {
                toast.success('User and all data deleted successfully');
                setDeleteUserId(null);
                setConfirmEmail('');
                loadUsers();
            } else {
                toast.error(res.error || 'Failed to delete user');
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.error || err?.message || 'Failed to delete user');
        } finally {
            setDeleting(false);
        }
    };

    const selectedUser = users.find(u => u.id === deleteUserId);
    const linkTargetUser = users.find((u) => u.id === linkUserId);
    const readWriteTargetUser = users.find((u) => u.id === readWriteWarningUserId);

    const handleLinkToAcademy = async () => {
        if (!linkUserId || !selectedAcademy) {
            toast.error('Select an academy');
            return;
        }
        setLinkSubmitting(true);
        try {
            const res = await adminBackofficeApi.linkUserToAcademy(linkUserId, {
                academyId: selectedAcademy.id,
                role: linkRole,
            });
            if (res.success) {
                toast.success(res.message || 'User linked to academy');
                setLinkUserId(null);
                setSelectedAcademy(null);
                setAcademyQuery('');
                setAcademyHits([]);
                loadUsers();
            } else {
                toast.error(res.error || 'Failed to link user');
            }
        } catch (err: unknown) {
            const msg =
                err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
                    : undefined;
            toast.error(msg || (err instanceof Error ? err.message : 'Failed to link user'));
        } finally {
            setLinkSubmitting(false);
        }
    };

    const handleImpersonate = async (userId: string, mode: ImpersonationMode) => {
        setImpersonatingUserId(userId);
        try {
            const { redirectPath } = await enterImpersonation(userId, mode);
            toast.success(
                mode === 'read_write'
                    ? 'Now viewing as user (read & write)'
                    : 'Now viewing as user (read-only)'
            );
            navigate(redirectPath);
        } catch (err: unknown) {
            const msg =
                err && typeof err === 'object' && 'response' in err
                    ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
                    : err instanceof Error
                      ? err.message
                      : 'Failed to start impersonation';
            toast.error(msg);
        } finally {
            setImpersonatingUserId(null);
            setReadWriteWarningUserId(null);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="User Management"
                help={<p>View platform users and start impersonation sessions.</p>}
            />

            <Card>
                <CardHeader className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
                    <h2 className="text-lg font-medium">All Users</h2>
                    <Field className="relative w-full md:w-72">
                        <FieldLabel htmlFor="users-search" className="sr-only">Search users</FieldLabel>
                        <Search className="absolute left-3 top-1/2 -transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input id="users-search" type="text" placeholder="Search by name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
                    </Field>
                </CardHeader>
                <CardContent>
                    {error && users.length === 0 ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
                            {error}
                        </div>
                    ) : (
                        <PaginatedTable<UserItem>
                            columns={[
                                {
                                    key: 'name',
                                    header: 'Name',
                                    render: (u) => (
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/backoffice/users/${u.id}`)}
                                                className="font-medium text-gray-900 dark:text-white hover:text-primary text-left"
                                            >
                                                {u.displayName}
                                            </button>
                                            <div className="text-sm text-gray-500">{u.id}</div>
                                        </div>
                                    )
                                },
                                {
                                    key: 'email',
                                    header: 'Email',
                                    render: (u) => u.email
                                },
                                {
                                    key: 'roles',
                                    header: 'Roles',
                                    render: (u) => {
                                        const roles = u.roles ?? (u.rolesWithAcademy ? [...new Set(u.rolesWithAcademy.map(r => r.role))] : []);
                                        if (roles.length === 0) return <span className="text-gray-400 text-sm">–</span>;
                                        return (
                                            <div className="flex flex-wrap gap-1">
                                                {roles.map((role) => (
                                                    <span
                                                        key={role}
                                                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${ROLE_BADGE_CLASS[role] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'}`}
                                                    >
                                                        {formatRole(role)}
                                                    </span>
                                                ))}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'academy',
                                    header: 'Academy',
                                    render: (u) => {
                                        const rwa = u.rolesWithAcademy ?? [];
                                        // Group by academy: academyId -> roles[]
                                        const byAcademy = new Map<string | null, string[]>();
                                        for (const { role, academyId } of rwa) {
                                            const key = academyId ?? null;
                                            const arr = byAcademy.get(key) ?? [];
                                            if (!arr.includes(role)) arr.push(role);
                                            byAcademy.set(key, arr);
                                        }
                                        if (byAcademy.size === 0) return <span className="text-gray-400 text-sm">–</span>;
                                        return (
                                            <div className="space-y-1 text-sm">
                                                {Array.from(byAcademy.entries()).map(([acId, roles]) => (
                                                    <div key={acId ?? 'platform'}>
                                                        <span className="font-medium text-gray-700 dark:text-gray-300">{acId ?? 'platform'}</span>
                                                        <span className="text-gray-500 dark:text-gray-400 ml-1">
                                                            ({roles.map(formatRole).join(', ')})
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    key: 'date',
                                    header: 'Joined',
                                    render: (u) => (u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '–'),
                                },
                                {
                                    key: 'actions',
                                    header: '',
                                    className: 'text-right',
                                    render: (u) => {
                                        const roles = u.roles ?? (u.rolesWithAcademy ? [...new Set(u.rolesWithAcademy.map(r => r.role))] : []);
                                        const isAdmin = roles.includes('admin');
                                        return (
                                        <div className="flex justify-end gap-1">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                                        disabled={isAdmin || impersonatingUserId === u.id}
                                                        title={isAdmin ? 'Cannot impersonate platform admins' : 'View as user'}
                                                    >
                                                        {impersonatingUserId === u.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Eye className="h-4 w-4" />
                                                        )}
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem
                                                        onClick={() => void handleImpersonate(u.id, 'read_only')}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                        View as user (read-only)
                                                    </DropdownMenuItem>
                                                    <DropdownMenuSeparator />
                                                    <DropdownMenuItem
                                                        className="text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400"
                                                        onClick={() => setReadWriteWarningUserId(u.id)}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                        View as user (read &amp; write)
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                                                onClick={() => {
                                                    setLinkUserId(u.id);
                                                    setLinkRole('guardian');
                                                    setSelectedAcademy(null);
                                                    setAcademyQuery('');
                                                    setAcademyHits([]);
                                                }}
                                                title="Link to academy"
                                            >
                                                <Link2 className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => setDeleteUserId(u.id)}
                                                disabled={(u.roles ?? []).includes('admin')} // Protect admins from accidental deletion
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        );
                                    }
                                }
                            ]}
                            data={users}
                            pagination={pagination}
                            loading={loading}
                            emptyMessage="No users found."
                            onPageChange={setPage}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Delete Confirmation Modal */}
            <Modal
                isOpen={!!deleteUserId}
                onClose={() => {
                    if (!deleting) {
                        setDeleteUserId(null);
                        setConfirmEmail('');
                    }
                }}
                title="Delete User & All Data"
            >
                <div className="space-y-4">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg flex items-start space-x-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                Warning: This action is permanent!
                            </p>
                            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                                This will delete the user profile, their academy, lessons, Stripe Connect account, and all associated student data. Platform billing customers in Stripe are anonymized immediately (subscription history is retained for bookkeeping). This cannot be undone.
                            </p>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            To proceed, please type the user's email <span className="font-semibold text-gray-900 dark:text-white">{selectedUser?.email}</span> below:
                        </p>
                        <Field className="mt-2">
                            <FieldLabel htmlFor="confirm-delete-email">Confirm email address</FieldLabel>
                            <Input id="confirm-delete-email" type="text" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder="Confirm email address" />
                        </Field>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setDeleteUserId(null);
                                setConfirmEmail('');
                            }}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outline"
                            className="bg-red-600 text-white hover:bg-red-700 hover:text-white border-none"
                            onClick={handleDeleteUser}
                            disabled={deleting || confirmEmail !== selectedUser?.email}
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Deleting...
                                </>
                            ) : (
                                'Permanently Delete Everything'
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!readWriteWarningUserId}
                onClose={() => {
                    if (!impersonatingUserId) {
                        setReadWriteWarningUserId(null);
                    }
                }}
                title="Read & write impersonation"
            >
                <div className="space-y-4">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg flex items-start space-x-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                You are about to impersonate with write access
                            </p>
                            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                                Actions you take will be performed as{' '}
                                <span className="font-semibold">
                                    {readWriteTargetUser?.displayName ?? 'this user'}
                                </span>
                                , including creating or changing bookings, lessons, settings, and payments.
                                Use read-only mode unless you need to reproduce a bug that requires making changes.
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => setReadWriteWarningUserId(null)}
                            disabled={!!impersonatingUserId}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outline"
                            className="bg-red-600 text-white hover:bg-red-700 hover:text-white border-none"
                            onClick={() =>
                                readWriteWarningUserId &&
                                void handleImpersonate(readWriteWarningUserId, 'read_write')
                            }
                            disabled={!!impersonatingUserId}
                        >
                            {impersonatingUserId ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Starting…
                                </>
                            ) : (
                                'Continue with read & write'
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={!!linkUserId}
                onClose={() => {
                    if (!linkSubmitting) {
                        setLinkUserId(null);
                        setSelectedAcademy(null);
                        setAcademyQuery('');
                        setAcademyHits([]);
                    }
                }}
                title="Link user to academy"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        {linkTargetUser ? (
                            <>
                                <span className="font-medium text-gray-900 dark:text-white">{linkTargetUser.displayName}</span>
                                <span className="text-gray-500"> ({linkTargetUser.email})</span>
                            </>
                        ) : null}
                    </p>

                    <Field>
                        <FieldLabel htmlFor="link-role">Role at this academy</FieldLabel>
                        <select
                            id="link-role"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                            value={linkRole}
                            onChange={(e) =>
                                setLinkRole(e.target.value as 'academy_owner' | 'coach' | 'guardian' | 'student')
                            }
                        >
                            {LINK_ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Academy owner updates the academy&apos;s owner field when it is unset. Coach creates a coach profile if needed.
                        </p>
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="academy-search">Find academy</FieldLabel>
                        <Input
                            id="academy-search"
                            type="text"
                            placeholder="Type name, slug, or paste academy UUID…"
                            value={academyQuery}
                            onChange={(e) => {
                                setAcademyQuery(e.target.value);
                                setSelectedAcademy(null);
                            }}
                            disabled={linkSubmitting}
                        />
                        {academySearchLoading && (
                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                            </p>
                        )}
                        {!selectedAcademy && debouncedAcademyQuery.trim().length >= 2 && academyHits.length > 0 && (
                            <ul className="mt-2 max-h-40 overflow-auto rounded-md border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                                {academyHits.map((a) => (
                                    <li key={a.id}>
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                                            onClick={() => {
                                                setSelectedAcademy(a);
                                                setAcademyQuery(`${a.name} (${a.slug})`);
                                            }}
                                        >
                                            <span className="font-medium text-gray-900 dark:text-white">{a.name}</span>
                                            <span className="text-gray-500 dark:text-gray-400 ml-2">{a.slug}</span>
                                            <div className="text-xs text-gray-400 font-mono">{a.id}</div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Field>

                    {selectedAcademy && (
                        <div className="rounded-md bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Selected:</span>{' '}
                            <span className="font-medium text-gray-900 dark:text-white">{selectedAcademy.name}</span>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-2">
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setLinkUserId(null);
                                setSelectedAcademy(null);
                                setAcademyQuery('');
                                setAcademyHits([]);
                            }}
                            disabled={linkSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleLinkToAcademy} disabled={linkSubmitting || !selectedAcademy}>
                            {linkSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Linking…
                                </>
                            ) : (
                                'Link account'
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
